import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { Pin, PhotoAnnotation } from '../types/photo.types';
import { VoicePinPlayer } from './shared/voice/VoicePinPlayer';

type Filter = 'all' | 'voice' | 'text';

interface AnnotationTranscriptRailProps {
  annotation: PhotoAnnotation;
  selectedPinId: string | null;
  onSelectPin: (pinId: string | null) => void;
  onMarkReviewed?: () => void;
  onStar?: () => void;
  isStarred?: boolean;
  isReviewed?: boolean;
}

export const AnnotationTranscriptRail: React.FC<AnnotationTranscriptRailProps> = ({
  annotation, selectedPinId, onSelectPin, onMarkReviewed, onStar, isStarred, isReviewed,
}) => {
  const [filter, setFilter] = useState<Filter>('all');
  const [playAllIndex, setPlayAllIndex] = useState<number | null>(null);

  const voicePins = useMemo(() => annotation.pins.filter(p => !!p.audioUrl), [annotation.pins]);
  const textPins = useMemo(() => annotation.pins.filter(p => !p.audioUrl), [annotation.pins]);

  const visible = useMemo(() => {
    if (filter === 'voice') return voicePins;
    if (filter === 'text') return textPins;
    return annotation.pins;
  }, [filter, annotation.pins, voicePins, textPins]);

  const startPlayAll = () => {
    if (voicePins.length === 0) return;
    setPlayAllIndex(0);
  };

  const advancePlayAll = () => {
    setPlayAllIndex(prev => {
      if (prev === null) return null;
      const next = prev + 1;
      return next >= voicePins.length ? null : next;
    });
  };

  // Drive pin selection when play-all advances. voicePins/onSelectPin are
  // intentionally omitted from deps — their churn would re-trigger selection
  // and cause feedback loops. Only playAllIndex should drive this effect.
  useEffect(() => {
    if (playAllIndex === null) return;
    const pin = voicePins[playAllIndex];
    if (pin) onSelectPin(pin.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playAllIndex]);

  // Cancel play-all when researcher manually selects a pin that isn't the
  // current play-all target.
  useEffect(() => {
    if (playAllIndex === null) return;
    const expected = voicePins[playAllIndex]?.id;
    if (selectedPinId !== expected) setPlayAllIndex(null);
  }, [selectedPinId, playAllIndex, voicePins]);

  // Cancel play-all when filter changes (current pin may no longer be rendered).
  useEffect(() => {
    setPlayAllIndex(null);
  }, [filter]);

  const copyTranscript = async (text: string) => {
    try { await navigator.clipboard.writeText(text); } catch { /* ignore */ }
  };

  return (
    <aside className="annotation-rail">
      <header className="annotation-rail-header">
        <div className="annotation-rail-title-row">
          <span className="annotation-rail-title">Annotations · {annotation.pins.length}</span>
          <button
            className="annotation-rail-play-all"
            onClick={startPlayAll}
            disabled={voicePins.length === 0}
          >
            <PlayGlyph size={10} /> Play all voice
          </button>
        </div>
        <div className="annotation-rail-filters">
          <FilterChip active={filter === 'all'} onClick={() => setFilter('all')}>
            All · {annotation.pins.length}
          </FilterChip>
          <FilterChip active={filter === 'voice'} onClick={() => setFilter('voice')}>
            <MicGlyph size={10} /> Voice · {voicePins.length}
          </FilterChip>
          <FilterChip active={filter === 'text'} onClick={() => setFilter('text')}>
            <TextGlyph size={10} /> Text · {textPins.length}
          </FilterChip>
        </div>
      </header>

      <ul className="annotation-rail-list">
        {visible.map(pin => (
          <PinRow
            key={pin.id}
            pin={pin}
            selected={pin.id === selectedPinId}
            autoPlay={playAllIndex !== null && voicePins[playAllIndex]?.id === pin.id}
            onSelect={() => onSelectPin(pin.id)}
            onAudioEnded={advancePlayAll}
            onCopy={() => copyTranscript(pin.transcript ?? pin.label ?? '')}
          />
        ))}
        {visible.length === 0 && (
          <li className="annotation-rail-empty">No annotations match this filter.</li>
        )}
      </ul>

      {(onMarkReviewed || onStar) && (
        <footer className="annotation-rail-footer">
          {onMarkReviewed && (
            <button className="annotation-rail-review" onClick={onMarkReviewed}>
              {isReviewed ? <><CheckGlyph size={11} /> Reviewed</> : 'Mark reviewed'}
            </button>
          )}
          {onStar && (
            <button
              className="annotation-rail-star"
              onClick={onStar}
              aria-label="Star"
              aria-pressed={!!isStarred}
            >
              <StarGlyph size={16} filled={!!isStarred} />
            </button>
          )}
        </footer>
      )}
    </aside>
  );
};

const FilterChip: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({
  active, onClick, children,
}) => (
  <button
    className={`annotation-rail-chip ${active ? 'active' : ''}`}
    onClick={onClick}
  >{children}</button>
);

interface PinRowProps {
  pin: Pin;
  selected: boolean;
  autoPlay: boolean;
  onSelect: () => void;
  onAudioEnded: () => void;
  onCopy: () => void;
}

const PinRow: React.FC<PinRowProps> = ({ pin, selected, autoPlay, onSelect, onAudioEnded, onCopy }) => {
  const ref = useRef<HTMLLIElement>(null);

  React.useEffect(() => {
    if (selected && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selected]);

  const isVoice = !!pin.audioUrl;

  return (
    <li
      ref={ref}
      className={`annotation-rail-row ${selected ? 'selected' : ''}`}
      onClick={onSelect}
    >
      <span className="annotation-rail-pin-num">{pin.index}</span>
      <div className="annotation-rail-body">
        <div className="annotation-rail-type">
          {isVoice
            ? <><MicGlyph size={10} /> Voice{pin.audioDuration ? ` · ${formatTime(pin.audioDuration)}` : ''}</>
            : <><TextGlyph size={10} /> Text</>}
        </div>

        {isVoice && (
          <VoicePinPlayer
            audioUrl={pin.audioUrl}
            duration={pin.audioDuration}
            transcript={pin.transcript}
            autoPlay={autoPlay}
            onEnded={onAudioEnded}
          />
        )}

        {isVoice && pin.label && (
          <div className="annotation-rail-voice-label">"{pin.label}"</div>
        )}

        {!isVoice && (
          <div className="annotation-rail-text-label">{pin.label}</div>
        )}

        {isVoice && !pin.transcript && (
          <div className="annotation-rail-pending">Transcribing…</div>
        )}
      </div>
      <button
        className="annotation-rail-copy"
        onClick={(e) => { e.stopPropagation(); onCopy(); }}
        aria-label="Copy"
      >
        <CopyGlyph size={14} />
      </button>
    </li>
  );
};

function formatTime(s: number): string {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

// Inline SVG glyphs — no emoji, per project convention.
const PlayGlyph: React.FC<{ size?: number }> = ({ size = 12 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden>
    <path d="M8 5v14l11-7z" />
  </svg>
);

const MicGlyph: React.FC<{ size?: number }> = ({ size = 12 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden>
    <path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v5a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11h-2z" />
  </svg>
);

const TextGlyph: React.FC<{ size?: number }> = ({ size = 12 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden>
    <path d="M5 4h14v3h-5v13h-4V7H5z" />
  </svg>
);

const CheckGlyph: React.FC<{ size?: number }> = ({ size = 12 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth={3} aria-hidden>
    <path d="M5 13l4 4L19 7" />
  </svg>
);

const StarGlyph: React.FC<{ size?: number; filled?: boolean }> = ({ size = 16, filled = false }) => (
  <svg viewBox="0 0 24 24" width={size} height={size}
    fill={filled ? '#D4A85A' : 'none'} stroke={filled ? '#D4A85A' : '#9A9490'} strokeWidth={1.8} aria-hidden>
    <path d="M12 3l2.7 5.9 6.3.6-4.8 4.3 1.4 6.2L12 17l-5.6 3 1.4-6.2L3 9.5l6.3-.6L12 3z" />
  </svg>
);

const CopyGlyph: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
    <rect x="8" y="8" width="12" height="12" rx="2" />
    <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" />
  </svg>
);
